/**
 * Sobrescrita de perna entre registros do mesmo plantão (spec 2026-09-24, decisões Q1–Q22).
 *
 * Regras PURAS do modal "Confirmar sobrescrita" — ficam fora do componente para o modal, o
 * composable e os testes contarem a mesma história. O backend revalida tudo (§6.3); aqui é
 * só o que evita um clique que vai voltar 422.
 */
import type { DSAttendanceLegOverwritePayload } from '@/Network/Types/Requests/DsAttendanceRequest'
import type {
  EffectiveValue,
  LegKey,
  LegOverwriteChoice,
  LegOverwriteConflict,
  LegOverwriteLeg,
  PriorityPaymentType,
  RecordKind
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'
import {
  getApiErrorMessage,
  getApiErrorStatus,
  hasApiErrorCode,
  unwrapApiError
} from '@/utils/apiError'
import { formatCurrency } from '@/utils/formatters'

/** Códigos estáveis do backend (§6.3). O texto mostrado sai daqui, não do `message`. */
export const LEG_OVERWRITE_ERROR_CODES = {
  CONFIRMATION_REQUIRED: 'OVERWRITE_CONFIRMATION_REQUIRED',
  BLOCKED_FROZEN: 'LEG_OVERWRITE_BLOCKED_FROZEN',
  JUSTIFICATION_REQUIRED: 'OVERWRITE_JUSTIFICATION_REQUIRED',
  INVALID_COMBINATION: 'INVALID_LEG_COMBINATION',
  DECISION_WITHOUT_CONFLICT: 'LEG_OVERWRITE_DECISION_WITHOUT_CONFLICT'
} as const

export const LEG_OVERWRITE_FROZEN_MESSAGE =
  'A sobrescrita está bloqueada: o pós-plantão deste plantão já foi aprovado. ' +
  'Reabra o pós-plantão e aprove este registro de novo.'

const JUSTIFICATION_REQUIRED_MESSAGE =
  'Informe a justificativa da sobrescrita: algum horário muda com a sua escolha.'

const INVALID_COMBINATION_MESSAGE =
  'Com essa combinação o check-out ficaria antes do check-in. Revise a escolha de cada perna.'

const INLINE_FALLBACK_MESSAGE =
  'Não foi possível confirmar a sobrescrita. Revise as escolhas e tente de novo.'

/* ------------------------------------------------------------------------- *
 * Fuso do plantão. O backend manda o OFFSET que o resolvedor grava (`-03:00`,
 * BE plan Spec deviation 2); nome IANA continua aceito por robustez. Offset é
 * deslocado à mão a partir do UTC (sem depender do fuso do navegador); IANA vai
 * por `Intl`. Ausente/inválido cai em `-03:00` (default do resolvedor).
 * ------------------------------------------------------------------------- */

export const FALLBACK_TIMEZONE = '-03:00'

const OFFSET_PATTERN = /^([+-])(\d{2}):(\d{2})$/

interface ZonedFields {
  day: string
  month: string
  hour: string
  minute: string
}

const pad2 = (value: number): string => String(value).padStart(2, '0')

function fieldsFromOffset(date: Date, offset: RegExpExecArray): ZonedFields {
  const sign = offset[1] === '-' ? -1 : 1
  const minutes = sign * (Number(offset[2]) * 60 + Number(offset[3]))
  const shifted = new Date(date.getTime() + minutes * 60_000)
  return {
    day: pad2(shifted.getUTCDate()),
    month: pad2(shifted.getUTCMonth() + 1),
    hour: pad2(shifted.getUTCHours()),
    minute: pad2(shifted.getUTCMinutes())
  }
}

function fieldsFromIana(date: Date, timeZone: string): ZonedFields | null {
  try {
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date)
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((item) => item.type === type)?.value ?? ''
    return { day: part('day'), month: part('month'), hour: part('hour'), minute: part('minute') }
  } catch {
    return null // nome inválido → quem chama cai no FALLBACK_TIMEZONE
  }
}

function zonedFields(iso: string, timeZone?: string | null): ZonedFields | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const zone = (timeZone ?? '').trim()
  const offset = OFFSET_PATTERN.exec(zone)
  if (offset) return fieldsFromOffset(date, offset)
  const fromIana = zone ? fieldsFromIana(date, zone) : null
  return (
    fromIana ?? fieldsFromOffset(date, OFFSET_PATTERN.exec(FALLBACK_TIMEZONE) as RegExpExecArray)
  )
}

export function formatTimeInZone(
  iso: string | null | undefined,
  timeZone?: string | null
): string | null {
  if (!iso) return null
  const fields = zonedFields(iso, timeZone)
  return fields ? `${fields.hour}:${fields.minute}` : null
}

export function formatDayMonthInZone(
  iso: string | null | undefined,
  timeZone?: string | null
): string | null {
  if (!iso) return null
  const fields = zonedFields(iso, timeZone)
  return fields ? `${fields.day}/${fields.month}` : null
}

export const LEG_LABELS: Record<LegKey, string> = {
  CHECKIN: 'Check-in',
  CHECKOUT: 'Check-out'
}

/** Nome de produto do registro — "Folha" sozinho não é vocabulário do auditor aqui. */
export const RECORD_KIND_LABELS: Record<RecordKind, string> = {
  FOLHA: 'Registro de Plantão',
  ATIVIDADE: 'Registro de Atividade'
}

export const PRIORITY_PAYMENT_LABELS: Record<PriorityPaymentType, string> = {
  A_VISTA: 'À vista',
  ANTECIPE: 'Antecipe'
}

export type LegDecisions = Partial<Record<LegKey, LegOverwriteChoice>>
export type DecisionsByStation = Record<number, LegDecisions>

/** Default = `NEW` em toda perna em conflito (Q5, Q17). */
export function buildDefaultDecisions(conflicts: LegOverwriteConflict[]): DecisionsByStation {
  const result: DecisionsByStation = {}
  for (const conflict of conflicts) {
    const decisions: LegDecisions = {}
    for (const leg of conflict.legs) decisions[leg.leg] = leg.defaultDecision ?? 'NEW'
    result[conflict.doctorStationId] = decisions
  }
  return result
}

export function choiceOf(decisions: LegDecisions | undefined, leg: LegKey): LegOverwriteChoice {
  return decisions?.[leg] ?? 'NEW'
}

/**
 * §6.3 passo 4 (Q7 corrigida pela Q22) — sobre as decisões ATUAIS: só quando alguma perna
 * `NEW` muda de horário. Pagamento prioritário sozinho é só aviso destacado.
 */
export function isJustificationRequired(
  conflict: LegOverwriteConflict,
  decisions: LegDecisions | undefined
): boolean {
  return conflict.legs.some((leg) => leg.timeChanges && choiceOf(decisions, leg.leg) === 'NEW')
}

export function requiresAnyJustification(
  conflicts: LegOverwriteConflict[],
  decisionsByStation: DecisionsByStation
): boolean {
  return conflicts.some((conflict) =>
    isJustificationRequired(conflict, decisionsByStation[conflict.doctorStationId])
  )
}

function resultingTime(leg: LegOverwriteLeg, choice: LegOverwriteChoice): string | null {
  return choice === 'NEW' ? leg.incoming.time : leg.current.time
}

/**
 * §6.3 passo 5 — só julga quando AS DUAS pernas estão em conflito e têm horário: com uma
 * perna só, a outra ponta é de um registro que o web não enxerga daqui (o backend responde
 * 422 `INVALID_LEG_COMBINATION` e o modal mostra inline).
 */
export function hasInvalidLegCombination(
  conflict: LegOverwriteConflict,
  decisions: LegDecisions | undefined
): boolean {
  const checkin = conflict.legs.find((leg) => leg.leg === 'CHECKIN')
  const checkout = conflict.legs.find((leg) => leg.leg === 'CHECKOUT')
  if (!checkin || !checkout) return false
  const inTime = resultingTime(checkin, choiceOf(decisions, 'CHECKIN'))
  const outTime = resultingTime(checkout, choiceOf(decisions, 'CHECKOUT'))
  if (!inTime || !outTime) return false
  const start = Date.parse(inTime)
  const end = Date.parse(outTime)
  if (Number.isNaN(start) || Number.isNaN(end)) return false
  return end < start
}

export function canConfirmLegOverwrite(
  conflicts: LegOverwriteConflict[],
  decisionsByStation: DecisionsByStation,
  justification: string
): boolean {
  if (conflicts.length === 0) return false
  if (conflicts.some((conflict) => conflict.frozen)) return false
  const invalid = conflicts.some((conflict) =>
    hasInvalidLegCombination(conflict, decisionsByStation[conflict.doctorStationId])
  )
  if (invalid) return false
  if (requiresAnyJustification(conflicts, decisionsByStation) && justification.trim() === '') {
    return false
  }
  return true
}

/**
 * D-W1: uma justificativa só, enviada a cada plantão que a exige. Com `justification = ''`
 * o resultado é o corpo da prévia (§6.2 — sem justificativa).
 */
export function buildLegOverwritesPayload(
  conflicts: LegOverwriteConflict[],
  decisionsByStation: DecisionsByStation,
  justification: string
): DSAttendanceLegOverwritePayload[] {
  const text = justification.trim()
  return conflicts.map((conflict) => {
    const chosen = decisionsByStation[conflict.doctorStationId]
    const decisions: LegDecisions = {}
    for (const leg of conflict.legs) decisions[leg.leg] = choiceOf(chosen, leg.leg)
    const item: DSAttendanceLegOverwritePayload = {
      doctorStationId: conflict.doctorStationId,
      decisions
    }
    if (text && isJustificationRequired(conflict, decisions)) item.justification = text
    return item
  })
}

/**
 * 409 `OVERWRITE_CONFIRMATION_REQUIRED` — "a prévia estava velha". Corpo
 * `{ statusCode, code, message, conflicts }` com `conflicts` no topo (§6.3 passo 3).
 * `null` = não é este erro.
 */
export function extractOverwriteConflicts(error: unknown): LegOverwriteConflict[] | null {
  if (getApiErrorStatus(error) !== 409) return null
  if (!hasApiErrorCode(error, LEG_OVERWRITE_ERROR_CODES.CONFIRMATION_REQUIRED)) return null
  const conflicts = (
    unwrapApiError(error) as { response?: { data?: { conflicts?: unknown } } } | null
  )?.response?.data?.conflicts
  return Array.isArray(conflicts) ? (conflicts as LegOverwriteConflict[]) : []
}

export function isLegOverwriteFrozenError(error: unknown): boolean {
  return (
    getApiErrorStatus(error) === 409 &&
    hasApiErrorCode(error, LEG_OVERWRITE_ERROR_CODES.BLOCKED_FROZEN)
  )
}

/** 422 — a decisão enviada é para plantão/perna que deixou de ter conflito (prévia velha). */
export function isDecisionWithoutConflictError(error: unknown): boolean {
  return (
    getApiErrorStatus(error) === 422 &&
    hasApiErrorCode(error, LEG_OVERWRITE_ERROR_CODES.DECISION_WITHOUT_CONFLICT)
  )
}

/** 422 da sobrescrita: fica DENTRO do modal (a digitação do auditor não se perde). */
export function legOverwriteInlineError(error: unknown): string | null {
  if (getApiErrorStatus(error) !== 422) return null
  if (hasApiErrorCode(error, LEG_OVERWRITE_ERROR_CODES.JUSTIFICATION_REQUIRED)) {
    return JUSTIFICATION_REQUIRED_MESSAGE
  }
  if (hasApiErrorCode(error, LEG_OVERWRITE_ERROR_CODES.INVALID_COMBINATION)) {
    return INVALID_COMBINATION_MESSAGE
  }
  return getApiErrorMessage(error, INLINE_FALLBACK_MESSAGE)
}

/** Horário de perna no fuso (offset) do plantão. */
export function formatLegTime(iso: string | null, timeZone?: string | null): string {
  return formatTimeInZone(iso, timeZone) ?? 'Sem horário'
}

/**
 * "À vista, concedido em 21/09" / "Antecipe, solicitado em 21/09" — data no fuso do plantão.
 * Spec §6.2 (regra canônica): À vista pela política vem com `grantedAt: null` → só "À vista";
 * no Antecipe a data é a do pedido (`antecipe_requested_at`), por isso "solicitado".
 */
export function priorityPaymentSummary(conflict: LegOverwriteConflict): string | null {
  const payment = conflict.priorityPayment
  if (!payment) return null
  const type = PRIORITY_PAYMENT_LABELS[payment.type] ?? payment.type
  const date = formatDayMonthInZone(payment.grantedAt, conflict.timezone)
  if (!date) return type
  const verb = payment.type === 'ANTECIPE' ? 'solicitado' : 'concedido'
  return `${type}, ${verb} em ${date}`
}

/** Valor antes → depois das decisões ATUAIS (a prévia é rechamada a cada toggle — D-W5). */
export function effectiveValueSummary(value: EffectiveValue | null | undefined): string | null {
  if (!value) return null
  const before = formatCurrency(value.before)
  if (value.after === value.before) return `Valor efetivo: ${before} (sem alteração)`
  return `Valor efetivo: ${before} → ${formatCurrency(value.after)}`
}
